/**
 * EF-CORE-UX-AND-QUOTE-FIX-V1: Searchable Client Select Component
 * 
 * A searchable, A-Z sorted client dropdown using react-select.
 * Provides type-to-filter, keyboard navigation, and clear selection.
 */
import React, { useMemo } from 'react';
import Select from 'react-select';

/**
 * ClientSelect - Searchable dropdown for client selection
 * 
 * @param {Array} clients - Array of client objects with {id, name}
 * @param {string} value - Currently selected client ID
 * @param {function} onChange - Callback when selection changes (receives client ID or null)
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Whether the select is disabled
 * @param {boolean} isClearable - Whether to show clear button
 * @param {string} className - Additional CSS classes
 * @param {string} testId - data-testid for testing
 */
export const ClientSelect = ({
  clients = [],
  value,
  onChange,
  placeholder = "Search or select client...",
  disabled = false,
  isClearable = true,
  className = "",
  testId = "client-select"
}) => {
  // Transform clients to react-select format and ensure A-Z sorting
  const options = useMemo(() => {
    return [...clients]
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'en', { sensitivity: 'base' }))
      .map(client => ({
        value: client.id,
        label: client.name || 'Unnamed Client'
      }));
  }, [clients]);

  // Find the currently selected option
  const selectedOption = useMemo(() => {
    if (!value) return null;
    return options.find(opt => opt.value === value) || null;
  }, [value, options]);

  // Handle selection change
  const handleChange = (selected) => {
    onChange(selected ? selected.value : null);
  };

  // Custom filter function for better search
  const filterOption = (option, inputValue) => {
    if (!inputValue) return true;
    const searchTerm = inputValue.toLowerCase();
    return option.label.toLowerCase().includes(searchTerm);
  };

  return (
    <div data-testid={testId} className={className}>
      <Select
        value={selectedOption}
        onChange={handleChange}
        options={options}
        placeholder={placeholder}
        isDisabled={disabled}
        isClearable={isClearable}
        isSearchable={true}
        filterOption={filterOption}
        classNamePrefix="react-select"
        inputId={`${testId}-trigger`}
        menuPlacement="auto"
        menuPosition="fixed"
        menuPortalTarget={document.body}
        // EVENTSFLO-PROJECT-CREATION-CORE-UX-BLOCKERS-FIX-V1
        // Radix Dialog (used by ProjectsPage New Project modal) locks
        // pointer-events on siblings of the dialog subtree. Since
        // react-select portals its menu into `document.body`, the menu
        // ends up OUTSIDE the dialog and its click handlers were being
        // silently dropped — clicks fell through to the shadcn Select
        // rendered immediately below the Client dropdown. Explicitly
        // restoring `pointerEvents: auto` on the portal + menu fixes
        // it, while keeping the high z-index for stacking above dialog
        // content and adjacent focus rings.
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
            pointerEvents: 'auto',
          }),
          menu: (base) => ({
            ...base,
            pointerEvents: 'auto',
          }),
          control: (base) => ({ ...base }),
          option: (base) => ({ ...base }),
        }}
        noOptionsMessage={() => "No clients found"}
        aria-label="Select client"
      />
      {/* Hidden marker so tests can read the selected client id
          without depending on react-select internals. */}
      <input
        type="hidden"
        data-testid={`${testId}-selected-value`}
        value={selectedOption ? selectedOption.value : ""}
        readOnly
      />
    </div>
  );
};

export default ClientSelect;
